<template>
  <div id="app" ref="hola" v-click-outside="clickOut">
    <div class="input-group">
      <input
        data-test="input-calendar"
        class="input-calendar"
        @click="openCalendar"
        readonly
        ref="weekInput"
        :v-model="weekSelected"
        :placeholder="label"
        :class="{ changed: !!weekSelected }"
        :danger="hasError"
      />
      <label>{{ label }}</label>
      <span v-if="weekSelected" class="week-range" @click="openCalendar">
        {{ dateRange }}
      </span>
      <button v-if="canRemove && weekSelected" class="input-clear" @click="clearWeek">
        <vs-icon icon="clear"></vs-icon>
      </button>
      <div v-if="hasError" class="con-text-validation span-text-validation-danger vs-input--text-validation-span">
        <span> {{ errorMsg }}</span>
      </div>
    </div>
    <transition name="fade">
      <div class="calendar" v-if="active">
        <div class="header">
          <a class="arrow" @click="movePreviousYear">&laquo;</a>
          <a class="arrow" @click="movePreviousMonth">&lsaquo;</a>
          <span class="title" @click="moveThisMonth">{{ header.label }}</span>
          <a class="arrow" @click="moveNextMonth">&rsaquo;</a>
          <a class="arrow" @click="moveNextYear">&raquo;</a>
        </div>
        <div class="weekdays">
          <div v-for="(weekday, index) in weekdays" :key="index" class="weekday">
              {{ weekday.label }}
          </div>
        </div>
        <div class="week" v-for="(week, index) in weeks" :key="index">
          <div
            v-for="(day, index) in week"
            :key="index"
            class="day"
            :class="{
              enabled: canSelect(day.weekInYear, day.YearForWeek),
              today: day.isToday,
              'not-in-month': !day.inMonth,
              selected: selectedWeek(day.weekInYear, day.YearForWeek),
              'is-hover': hoverOnWeek === day.weekInYear,
            }"
            @mouseover="hoverOnWeek = setHoverOn(day)"
            @click="selectWeek(day.weekInYear, day.YearForWeek, null, true)">
            {{ day.day }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// Calendar data
const _daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const _weekdayLabels = ["Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb.", "Dom."];
const _weekdayLength = 3;
const _weekdayCasing = "title";
const _monthLabels = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const _monthLength = 0;
const _monthCasing = "title";

// Helper function for label transformation
const _transformLabel = (label, length, casing) => {
  label = length <= 0 ? label : label.substring(0, length);
  if (casing.toLowerCase() === "lower") return label.toLowerCase();
  if (casing.toLowerCase() === "upper") return label.toUpperCase();
  return label;
};

// Today's data
const _today = new Date();
const _todayComps = {
  year: _today.getFullYear(),
  month: _today.getMonth() + 1,
  day: _today.getDate(),
};

import Vue from "vue";

Vue.directive("click-outside", {
  bind: function (el, binding, vnode) {
    this.event = function (event) {
      if (!(el == event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener("click", this.event);
  },
  unbind: function (el) {
    document.body.removeEventListener("click", this.event);
  },
});

export default {
  name: "we-week-picker",
  props: {
    value: { type: null },
    label: {
      type: String,
      required: false,
      default: "WeekPicker",
    },
    errorMsg: {
      type: String,
      required: false,
      default: "eee",
    },
    hasError: {
      type: Boolean,
      required: false,
      default: false,
    },
    minWeek: {
      type: undefined,
      required: false,
    },
    canRemove: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      month: _todayComps.month,
      year: _todayComps.year,
      weekSelected: "",
      active: false,
      hoverOnWeek: "",
    };
  },
  directives: {
    "click-outside": {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          // here I check that click was outside the el and his childrens
          if (!(el == event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  computed: {
    dateRange() {
      if (this.weekSelected) {
        const week = this.weekSelected.split("-")[0];
        const year = this.weekSelected.split("-")[1];
        return `Del ${this.getFirstDayOfWeek(
          week,
          year
        )} al ${this.getLastDayOfWeek(week, year)}`;
      }
      return "";
    },

    // Our component exposes month as 1-based, but sometimes we need 0-based
    monthIndex() {
      return this.month - 1;
    },

    // State for calendar header (no dependencies yet...)
    months() {
      return _monthLabels.map((ml, i) => ({
        label: ml,
        label_1: ml.substring(0, 1),
        label_2: ml.substring(0, 2),
        label_3: ml.substring(0, 3),
        number: i + 1,
      }));
    },

    // State for weekday header (no dependencies yet...)
    weekdays() {
      return _weekdayLabels.map((wl, i) => ({
        label: wl,
        label_1: wl.substring(0, 1),
        label_2: wl.substring(0, 2),
        label_3: wl.substring(0, 3),
        number: i + 1,
      }));
    },

    // State for calendar header
    header() {
      const month = this.months[this.monthIndex];
      return {
        month,
        year: this.year.toString(),
        shortYear: this.year.toString().substring(2, 4),
        label: month.label + " " + this.year,
      };
    },

    // Returns number for first weekday (1-7), starting from Monday
    firstWeekdayInMonth() {
      return new Date(this.year, this.monthIndex, 0).getDay() + 1;
    },

    // Returns number of days in the current month
    daysInMonth() {
      // Check for February in a leap year
      if (this.month === 2 && this.isLeapYear) return 29;
      // ...Just a normal month
      return _daysInMonths[this.monthIndex];
    },

    weeks() {
      const weeks = [];
      let previousMonth = true,
          thisMonth = false,
          nextMonth = false;
      let day = this.previousMonthComps.days - this.firstWeekdayInMonth + 2;
      let month = this.previousMonthComps.month;
      let year = this.previousMonthComps.year;
      // Cycle through each week of the month, up to 6 total
      for (let w = 1; w <= 6 && !nextMonth; w++) {
        // Cycle through each weekday
        const week = [];
        for (let d = 1; d <= 7; d++) {
          // We need to know when to start counting actual month days
          if (previousMonth && d >= this.firstWeekdayInMonth) {
              // Reset day/month/year counters
              day = 1;
              month = this.month;
              year = this.year;
              // ...and flag we're tracking actual month days
              previousMonth = false;
              thisMonth = true;
          }

          // Append day info for the current week
          // Note: this might or might not be an actual month day
          //  We don't know how the UI wants to display various days,
          //  so we'll supply all the data we can
          var date = new Date(year, month - 1, day - 1);
          week.push({
            label: day && thisMonth ? day.toString() : "",
            day,
            weekday: d,
            week: w,
            weekInYear: this.getWeekNumber(date),
            YearForWeek: this.getWeekYearNumber(date),
            month,
            year,
            date: date,
            beforeMonth: previousMonth,
            afterMonth: nextMonth,
            inMonth: thisMonth,
            isToday:
              day === _todayComps.day &&
              month === _todayComps.month &&
              year === _todayComps.year,
            isFirstDay: thisMonth && day === 1,
            isLastDay: thisMonth && day === this.daysInMonth,
          });

          // We've hit the last day of the month
          if (thisMonth && day >= this.daysInMonth) {
            thisMonth = false;
            nextMonth = true;
            day = 1;
            month = this.nextMonthComps.month;
            year = this.nextMonthComps.year;
            // Still in the middle of the month (hasn't ended yet)
          } else {
            day++;
          }
        }
        // Append week info for the month
        weeks.push(week);
      }
      return weeks;
    },

    isLeapYear() {
      return ((this.year % 4 === 0 && this.year % 100 !== 0) || this.year % 400 === 0);
    },

    // Data components for previous month
    previousMonthComps() {
      if (this.month === 1) {
        return {
          days: _daysInMonths[11],
          month: 12,
          year: this.year - 1,
        };
      }
      return {
        days: this.month === 3 && this.isLeapYear ? 29 : _daysInMonths[this.monthIndex - 1],
        month: this.month - 1,
        year: this.year,
      };
    },

    // Data components for next month
    nextMonthComps() {
      if (this.month === 12) {
        return {
          days: _daysInMonths[0],
          month: 1,
          year: this.year + 1,
        };
      }
      return {
        days: this.month === 1 && this.isLeapYear ? 29 : _daysInMonths[this.monthIndex + 1],
        month: this.month + 1,
        year: this.year,
      };
    },
  },

  methods: {
    getFirstDayOfWeek(week, year) {
      var date = this.getDateOfWeek(week, year);
      date = date.toJSON().split("T")[0].split("-");
      return `${date[2]}/${date[1]}/${date[0]}`;
    },

    getLastDayOfWeek(week, year) {
      var date = this.getDateOfWeek(week, year);
      date.setDate(date.getDate() + (6));
      date = date.toJSON().split("T")[0].split("-");
      return `${date[2]}/${date[1]}/${date[0]}`;
    },

    // CAMBIO EN EL CALENDARIO PARA QUE EL DATEPICKER RESPETE SEMANAS SEGUN ISO TOMANDO COMO INICIO EL DIA LUNES
    getDateOfWeek(week, year) {
      let day = new Date(year, 0, 1 + (week - 1) * 7);
      let dayOfWeek = day.getDay();
      let weekStart = day;
      if (dayOfWeek <= 7) {
        weekStart.setDate(day.getDate() - day.getDay() + 1);
      } else {
        weekStart.setDate(day.getDate() + 8 - day.getDay());
      }
      if (year == 2023) {
        weekStart.setDate(weekStart.getDate() - (7));
      }
      return weekStart;
    },

    setHoverOn(day) {
      if (this.minWeek) {
        if (this.minWeek.split("-")[1] > day.year) {
          return "";
        }
        if (this.minWeek.split("-")[1] == day.year && this.minWeek.split("-")[0] > day.weekInYear) {
          return "";
        }
      }
      this.weekNumber = day.weekInYear;
      return day.weekInYear;
    },

    moveThisMonth() {
      this.month = _todayComps.month;
      this.year = _todayComps.year;
    },

    moveNextMonth() {
      const { month, year } = this.nextMonthComps;
      this.month = month;
      this.year = year;
    },

    movePreviousMonth() {
      const { month, year } = this.previousMonthComps;
      this.month = month;
      this.year = year;
    },

    moveNextYear() {
      this.year++;
    },

    movePreviousYear() {
      this.year--;
    },

    // OBTENER NUMERO DE SEMANA PARTIENDO DESDE EL DIA LUNES
    getWeekNumber(date) {
      var d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      d.setUTCDate(d.getUTCDate() + 7 - d.getUTCDay());
      var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      var weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return weekNo;
    },

    // OBTENER AÑO PARTIENDO DESDE EL NUMERO DE SEMANAS
    getWeekYearNumber(date) {
      var date_1 = new Date(2022, 11, 25);
      var date_2 = new Date(2023, 0, 1);
      if (date >= date_1 && date <= date_2) {
        return 2023
      }
      var d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      d.setUTCDate(d.getUTCDate() + 7 - d.getUTCDay());
      return d.getFullYear();
    },

    selectWeek(weekN, year, privilege = null, internalSelected = false) {
      // La variable de privilegio se utilizó solamente para poder actualizar la minWeek
      // Debido a que la validación si es posible setear la fecha se realiza despues.

      // La variable internalSelected determina si la fecha se cambio por dentro o fuera del componente

      if (this.canSelect(weekN, year) || privilege === true) {
        this.weekSelected = `${weekN.toString().padStart(2, 0)}-${year}`;
        this.$emit("input", this.weekSelected);
        if(internalSelected) {
          this.$emit('internalInputChanged', this.weekSelected);
        }
        this.$refs.weekInput.value = this.weekSelected;
        this.active = false;
      }
    },

    selectedWeek(week, year) {
      if (this.weekSelected) {
        if (Number(this.weekSelected.split("-")[1]) == year) {
          return Number(this.weekSelected.split("-")[0]) == week;
        }
      }
      return false;
    },

    canSelect(weekN, year) {
      if (this.minWeek) {
        if (this.minWeek.split("-")[1] > year) {
          return false;
        }
        if (this.minWeek.split("-")[1] == year && this.minWeek.split("-")[0] > weekN) {
          return false;
        }
      }
      return true;
    },

    clearWeek() {
      this.weekSelected = null;
      this.$emit("input", null);
      this.$refs.weekInput.value = null;
    },

    openCalendar() {
      this.active = true;
    },

    clickOut() {
      if (this.active) {
          this.active = false;
      }
    },
  },
};
</script>

<style scoped>
.week-range {
  position: absolute;
  left: 78px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  font-size: 0.9rem;
  padding-left: 15px;
  border-left: 1px solid #f1f1f1;
}

.small .week-range {
  padding-left: 0;
  left: 65px;
  font-size: 0.85rem;
  width: 145px;
  overflow: hidden;
  white-space: nowrap;
  text-indent: -18px;
  display: block;
}

.input-calendar ~ .input-clear {
  position: absolute;
  top: 0;
  z-index: 3;
  color: #696969;
  right: 0;
  height: 100%;
  background: none;
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.input-calendar ~ .input-clear::after {
  content: "";
  position: absolute;
  background: rgba(0, 0, 0, 0.3);
  top: 5px;
  left: 7px;
  bottom: 0;
  right: 0;
  height: 30px;
  width: 30px;
  border-radius: 20px;
  -webkit-transform: scale(0);
  transform: scale(0);
  transition: all 0.3s ease;
}

.input-calendar ~ .input-clear:hover {
  color: white;
}

.input-calendar ~ .input-clear:hover::after {
  -webkit-transform: scale(0.7);
  transform: scale(0.7);
}

.input-calendar ~ .input-clear i {
  font-size: 0.9rem;
  z-index: 4;
}

.calendar {
  position: absolute;
  z-index: 4;
  display: flex;
  flex-direction: column;
  -webkit-box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  background: white;
  font-family: Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, Oxygen,
      Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
}

.header {
  display: flex;
  justify-content: stretch;
  align-items: center;
  color: white;
  background-color: #01143c;
  border-top-left-radius: 0.3rem;
  border-top-right-radius: 0.3rem;
}

.header .arrow {
  cursor: pointer;
  padding: 0 0.4em 0.2em 0.4em;
  font-size: 1.8rem;
  font-weight: 500;
  user-select: none;
  flex-grow: 0;
  font-family: "Source Sans Pro", sans-serif;
}

.header .arrow:hover {
  color: #dcdcdc;
}

.header .title {
  cursor: pointer;
  flex-grow: 1;
  font-size: 1rem;
  text-align: center;
  min-width: 150px;
}

.header .title:hover {
  color: #dcdcdc;
}

.weekdays {
  display: flex;
  flex: auto;
}

.weekday {
  width: 14.2857%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.4rem 0;
  color: #2c3e50;
  border-style: solid;
  border: none;
  font-size: 12px;
  text-transform: lowercase;
}

.week {
  display: flex;
  padding: 3px 0;
}

.week:last-of-type {
  padding: 3px 0 0 0;
}

.day {
  width: 14.2857%;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #3a3a3a;
  background-color: white;
  transition: all 0.1s ease;
  font-size: 0.8rem;
  cursor: pointer;
}

.day:not(.enabled) {
  color: #ccc;
}

.day.today {
  color: black;
  background-color: #eaeaea;
  border-radius: 6px;
}

.calendar .week:last-of-type .day:first-of-type {
  border-bottom-left-radius: 0.3rem;
}

.calendar .week:last-of-type .day:last-of-type {
  border-bottom-right-radius: 0.3rem;
}

.not-in-month {
  color: #cacaca;
  background-color: #fafafa;
}

.not-in-month.enabled {
  color: #949494;
}

.is-hover,
.enabled.is-hover,
.enabled.not-in-month.is-hover {
  color: #fafafa;
  background-color: #cacaca;
}

.selected,
.not-in-month.selected {
  color: #fafafa;
  background-color: #2f313a;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10%);
}
</style>
